import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ssoService from '../services/ssoService';
import './LandingPage.css';

function LandingPage() {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setIsAuthenticating(true);
    
    try {
      await ssoService.signInWithGoogle();
      // Authentication successful, navigate to /dashboard
      navigate('/dashboard');
    } catch (error) {
      console.error('Authentication failed:', error);
      alert('Failed to sign in with Google. Please try again.');
      setIsAuthenticating(false);
    }
  };

  return (
    <div className="landing-page">
      {/* Sign In Modal */}
      <div className={`modal-overlay ${!showModal ? 'hidden' : ''}`}>
        <div className="modal-backdrop animate-fade-in" onClick={() => setShowModal(false)}></div>
        <div className="modal-content animate-slide-up">
          <div className="modal-header-bar"></div>

          <div className="modal-title">
            <h2>Welcome Back</h2>
            <p>Sign in to access your intelligent workspace</p>
          </div>

          <form onSubmit={handleSignIn}>
            <button 
              type="submit" 
              disabled={isAuthenticating}
              className="btn btn-primary" 
              style={{ 
                width: '100%', 
                marginTop: '1rem', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                gap: '0.75rem',
                opacity: isAuthenticating ? 0.7 : 1,
                cursor: isAuthenticating ? 'not-allowed' : 'pointer'
              }}
            >
              {isAuthenticating ? (
                <>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                    <circle cx="12" cy="12" r="10" strokeDasharray="32" strokeDashoffset="8"/>
                  </svg>
                  Signing in...
                </>
              ) : (
                <>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  Sign in with Google
                </>
              )}
            </button>
          </form>

          <div className="signup-text">
            Don't have an account? <button className="signup-link">Contact Sales</button>
          </div>
        </div>
      </div>

      {/* Main Landing Page */}
      <div>
        {/* Dynamic Background */}
        <div className="background-container">
          <div className="bg-blob-1 animate-pulse-slow"></div>
          <div className="bg-blob-2"></div>
        </div>

        {/* Navigation */}
        <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
          <div className="navbar-container">
            <div className="navbar-logo">
              <div className="logo-icon">
                <span>E</span>
              </div>
              <span>EVA</span>
            </div>

            <div className="navbar-links">
              <a href="#features">Features</a>
              <a href="#security">Security</a>
              <a href="#enterprise">Enterprise</a>
              <a href="#pricing">Pricing</a>
            </div>

            <div className="navbar-actions">
              <button className="btn btn-ghost btn-sm" style={{ display: 'none' }}>Book Demo</button>
              <button onClick={() => setShowModal(true)} className="btn btn-primary btn-sm">Sign In</button>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="hero-section">
          <div className="hero-container">
            <div className="hero-badge animate-fade-in-up">
              <span className="pulse-dot">
                <span className="pulse-dot-outer animate-ping"></span>
                <span className="pulse-dot-inner"></span>
              </span>
              Now with Meeting Dossiers 2.0
            </div>

            <h1 className="hero-title animate-fade-in-up delay-100">
              Your Schedule, <br />
              <span className="gradient-text">
                Orchestrated by Intelligence
              </span>
            </h1>

            <p className="hero-description animate-fade-in-up delay-200">
              EVA isn't just a calendar. It's an executive assistant that builds dossiers, prepares you for meetings, and manages your time via natural conversation.
            </p>

            <div className="hero-actions animate-fade-in-up delay-300">
              <button onClick={() => setShowModal(true)} className="btn btn-primary btn-lg">
                Get Started Free
              </button>
              <button className="btn btn-secondary btn-lg">
                View Capabilities
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <path d="m9 11 3 3L22 4"/>
                </svg>
              </button>
            </div>

            {/* Hero Visual - Dashboard Preview */}
            <div className="dashboard-preview animate-float">
              <div className="dashboard-header-line"></div>

              {/* Mock UI Header */}
              <div className="dashboard-header">
                <div className="window-controls">
                  <div className="window-dot red"></div>
                  <div className="window-dot yellow"></div>
                  <div className="window-dot green"></div>
                </div>
                <div className="window-title"></div>
              </div>

              {/* Mock UI Content */}
              <div className="dashboard-content">
                {/* Sidebar Mock */}
                <div className="dashboard-sidebar">
                  <div className="sidebar-icon active"></div>
                  <div className="sidebar-items">
                    <div className="sidebar-icon inactive"></div>
                    <div className="sidebar-icon inactive"></div>
                    <div className="sidebar-icon inactive"></div>
                    <div className="sidebar-icon inactive"></div>
                  </div>
                </div>

                {/* Chat Mock */}
                <div className="dashboard-chat">
                  <div className="chat-header">
                    <div className="chat-avatar">AI</div>
                    <div className="placeholder-bar" style={{ width: '6rem' }}></div>
                  </div>
                  <div className="chat-messages">
                    <div className="chat-message ai">
                      <div className="placeholder-bar w-full mb-2"></div>
                      <div className="placeholder-bar w-1-2"></div>
                    </div>
                    <div className="chat-message user">
                      <div className="placeholder-bar w-full mb-2"></div>
                      <div className="placeholder-bar w-2-3"></div>
                    </div>
                  </div>
                  <div className="chat-input"></div>
                </div>

                {/* Calendar Mock */}
                <div className="dashboard-calendar">
                  <div className="calendar-grid">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="calendar-card">
                        <div className="placeholder-bar mb-3" style={{ width: '4rem' }}></div>
                        <div className="placeholder-bar w-full mb-2"></div>
                        <div className="placeholder-bar w-2-3"></div>
                        <div style={{
                          position: 'absolute',
                          bottom: '1rem',
                          right: '1rem',
                          height: '1.5rem',
                          width: '1.5rem',
                          borderRadius: '50%',
                          background: 'linear-gradient(to bottom right, #8b5cf6, #4f46e5)',
                          opacity: 0.2
                        }}></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Overlay Gradient */}
              <div className="dashboard-overlay"></div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="features-section" id="features">
          <div className="features-container">
            <div className="features-header">
              <h2>Intelligence built for Executives</h2>
              <p>Seamlessly integrates with your existing ecosystem to provide context-aware scheduling.</p>
            </div>

            <div className="features-grid">
              <div className="feature-card">
                <div className="feature-card-overlay"></div>
                <div className="feature-content">
                  <div className="feature-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                    </svg>
                  </div>
                  <h3>Natural Language Scheduling</h3>
                  <p>
                    Just tell EVA to 'Clear my afternoon' or 'Find time with Sarah next week'. It handles the negotiation.
                  </p>
                </div>
              </div>

              <div className="feature-card">
                <div className="feature-card-overlay"></div>
                <div className="feature-content">
                  <div className="feature-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 3v18h18"/>
                      <path d="m19 9-5 5-4-4-3 3"/>
                    </svg>
                  </div>
                  <h3>Meeting Dossiers</h3>
                  <p>
                    EVA scrapes emails, files, and public data to generate a one-page briefing before every VIP meeting.
                  </p>
                </div>
              </div>

              <div className="feature-card">
                <div className="feature-card-overlay"></div>
                <div className="feature-content">
                  <div className="feature-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/>
                    </svg>
                  </div>
                  <h3>Secure by Default</h3>
                  <p>
                    Enterprise-grade encryption and privacy controls ensure your proprietary data never leaves your control.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="footer">
          <div className="footer-container">
            <div className="footer-logo">
              <div className="footer-logo-icon">E</div>
              <span>EVA</span>
            </div>
            <div className="footer-copyright">
              © 2024 EVA AI Inc. All rights reserved.
            </div>
            <div className="footer-links">
              <a href="#privacy">Privacy</a>
              <a href="#terms">Terms</a>
              <a href="#twitter">Twitter</a>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default LandingPage;
